import { useCallback, useMemo } from 'react';
import { sprintf } from 'sprintf-js';

import type { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import { Box } from 'components/Box/Box';
import { BulletList } from 'components/BulletList/BulletList';
import { Icon } from 'components/Icon/Icon';
import { InfoList } from 'components/InfoList/InfoList';
import { StyledNativeLinkWithRef } from 'components/Link/StyledNativeLinkWithRef';
import { Slideshow } from 'components/Slideshow/Slideshow';
import { useViewportBreakpointValue } from 'hooks/viewport/useViewportBreakpointValue';
import type { ApiOrg } from 'modules/listing/api/org/types/ApiOrg';
import { ListingViewSection } from 'modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingRichTextTruncatedHtmlWithLinks } from 'modules/listing/components/RichText/ListingRichTextTruncatedHtmlWithLinks';
import { trackAnchorClicked } from 'utils/analytics/track/trackAnchorClicked';
import { showModal } from 'zustand-stores/modalStore';

import { ViewOrgPageListingsTabs } from './ViewOrgPageListingsTabs';
import { ViewOrgPageListingsTabsMobile } from './ViewOrgPageListingsTabsMobile';
import type { ViewOrgPageListingTab } from './types/ViewOrgPageListingTab';
import { orgHasListings } from './utils/orgHasListings';
import { orgPresentListingTypes } from './utils/orgPresentListingTypes';

type Props = {
  org: ApiOrg;
  areasOfFocus: ApiAreaOfFocus[] | undefined;
};

export function ViewOrgPageDetails({ org, areasOfFocus }: Props) {
  const areas = areasOfFocus || org.areasOfFocus;

  const isMobileTabs = useViewportBreakpointValue({ xs: true, sm: false });

  const headerTitle = useMemo(() => {
    const hasPhoto = org.slideshowItems.find((item) => item.type === 'IMAGE');
    const hasVideo = org.slideshowItems.find((item) => item.type === 'VIDEO');

    const orgName = org.name;

    if (hasPhoto && !hasVideo) {
      return sprintf(getText('Photos for %(orgName)s'), { orgName });
    }

    if (hasVideo && !hasPhoto) {
      return sprintf(getText('Videos for %(orgName)s'), { orgName });
    }

    return sprintf(getText('Photos & Videos for %(orgName)s'), { orgName });
  }, [org.name, org.slideshowItems]);

  const openMediaGallery = useCallback(
    ({ itemIndex }: { itemIndex: number }) => {
      showModal(
        'MEDIA_GALLERY',
        // @ts-expect-error TS(2322): Type 'SlideshowData[]' is not assignable to type '... Remove this comment to see the full error message
        { items: org.slideshowItems, itemIndex },
      );
    },
    [org.slideshowItems],
  );

  const defaultTab: ViewOrgPageListingTab = useMemo(
    () => orgPresentListingTypes(org)[0] || 'JOB',
    [org],
  );

  return (
    <Box px={0} mb={2}>
      {org.mission !== null && org.missionTruncated !== null && (
        <ListingViewSection id="mission" title={getText('Mission')}>
          <ListingRichTextTruncatedHtmlWithLinks
            html={org.mission}
            truncatedHtml={org.missionTruncated}
            showMoreLinkTitle={getText('Read more about this organization')}
            showLessLinkTitle={getText('Read less about this organization')}
          />
        </ListingViewSection>
      )}
      {org.description !== null && org.descriptionTruncated !== null && (
        <ListingViewSection id="about" title={getText('About Us')}>
          <ListingRichTextTruncatedHtmlWithLinks
            html={org.description}
            truncatedHtml={org.descriptionTruncated}
            showMoreLinkTitle={getText('Read more about this organization')}
            showLessLinkTitle={getText('Read less about this organization')}
          />
        </ListingViewSection>
      )}
      {areas.length > 0 && (
        <ListingViewSection title={getText('Cause Areas Include')}>
          <BulletList
            columns={2}
            itemMargin={32}
            items={areas.map((area) => ({
              key: area,
              value: area,
            }))}
          />
        </ListingViewSection>
      )}
      <ListingViewSection
        title={
          org.phone || org.email
            ? getText('Location & Contact')
            : getText('Location')
        }
      >
        <InfoList $columns={1} $noSvgStyles>
          {org.phone && (
            <li>
              <Box display="inline-flex" mr="6px">
                <Icon name="cell-phone" size={24} />
              </Box>
              <StyledNativeLinkWithRef
                href={`tel:${org.phone.replace(/[^0-9+]/g, '')}`}
                onClick={(event) =>
                  trackAnchorClicked(event, 'Organization phone number', {
                    method: 'Phone',
                  })
                }
              >
                {org.phone}
              </StyledNativeLinkWithRef>
            </li>
          )}

          {org.email && (
            <li>
              <Box display="inline-flex" mr="6px">
                <Icon name="mail" size={24} />
              </Box>

              <StyledNativeLinkWithRef
                href={`mailto:${org.email}`}
                target="_blank"
                onClick={(event) =>
                  trackAnchorClicked(event, 'Organization email', {
                    method: 'Email',
                  })
                }
              >
                <Box whiteSpace="nowrap">{org.email}</Box>
              </StyledNativeLinkWithRef>
            </li>
          )}

          {org.address && (
            <li>
              <Box display="inline-flex" mr="6px">
                <Icon name="location" size={24} />
              </Box>

              <div>
                {org.address.full}
                {org.address.line2 && <br />}
                {org.address.line2}
              </div>
            </li>
          )}
        </InfoList>
      </ListingViewSection>

      {org.slideshowItems.length > 0 && (
        <ListingViewSection title={headerTitle}>
          <Box mx="64px">
            <Slideshow
              carouselId="org-photos-carousel"
              items={org.slideshowItems}
              data-qa-id="org-carousel"
              onClickIndex={openMediaGallery}
              size={200}
            />
          </Box>
        </ListingViewSection>
      )}

      {orgHasListings(org) && (
        <Box id="opportunities" data-qa-id="opportunities">
          <ListingViewSection title={getText('Current listings')}>
            {isMobileTabs ? (
              <ViewOrgPageListingsTabsMobile
                org={org}
                defaultTab={defaultTab}
              />
            ) : (
              <ViewOrgPageListingsTabs org={org} defaultTab={defaultTab} />
            )}
          </ListingViewSection>
        </Box>
      )}
    </Box>
  );
}
